/**
 * sceneLab — NSPP's "best buttons/tabs" demo page.
 *
 * One scene, four interactive modes, switchable via tap-able tabs at the
 * bottom of the screen. Each mode showcases a unique NSPP capability
 * that no other web renderer can do:
 *
 *   1. CHASE  — cursor leaves a trail of SDF ghosts that follow with
 *               per-node delay. As they catch up, they bloom via the
 *               per-instance filter chain (saturate + brightness).
 *   2. PAINT  — click+drag leaves a stroke of SDF-rounded rects. The
 *               stroke fades out over ~2s. Per-segment color sampled
 *               from velocity. Motion blur naturally smears fast strokes.
 *   3. ORBIT  — pointer creates a gravity well that pulls floating SDF
 *               particles toward the cursor. Uses inverse-distance pull
 *               strength.
 *   4. REEL   — a horizontal reel of cards the user can flick through.
 *               Each card has a per-instance hue-rotate filter for visual
 *               diversity.
 *
 * Pointer state is fed in by main.ts via the scene's `__labPointer` side
 * door (avoids the engine→scene circular import).
 */

import {
  type Scene,
  type SceneNode,
  type RGBA,
  type Filter,
  createScene,
  makeRect,
  makeText,
  addNode,
} from "../sceneGraph";

type Mode = "chase" | "paint" | "orbit" | "reel" | "video" | "burst" | "compose" | "audio" | "capture";

interface ModeSpec {
  key: Mode;
  label: string;
  descriptor: string;
  gradient: [RGBA, RGBA];
}

const MODES: ModeSpec[] = [
  { key: "chase", label: "Chase", descriptor: "Move your cursor — SDF ghosts chase the pointer",
    gradient: [{ r: 0.98, g: 0.57, b: 0.24, a: 1 }, { r: 0.93, g: 0.28, b: 0.6, a: 1 }] },
  { key: "paint", label: "Paint", descriptor: "Click + drag — paint a stroke that fades out",
    gradient: [{ r: 0.2, g: 0.83, b: 0.6, a: 1 }, { r: 0.02, g: 0.71, b: 0.83, a: 1 }] },
  { key: "orbit", label: "Orbit", descriptor: "Move your cursor — particles get pulled toward you",
    gradient: [{ r: 0.65, g: 0.55, b: 0.98, a: 1 }, { r: 0.93, g: 0.28, b: 0.6, a: 1 }] },
  { key: "reel",  label: "Reel",  descriptor: "Click + drag horizontally — flick through cards",
    gradient: [{ r: 0.98, g: 0.75, b: 0.14, a: 1 }, { r: 0.93, g: 0.28, b: 0.6, a: 1 }] },
  { key: "video", label: "Video Wall", descriptor: "Drop an MP4 — 64-tile live mosaic at 60 FPS",
    gradient: [{ r: 0.16, g: 0.78, b: 0.55, a: 1 }, { r: 0.06, g: 0.36, b: 0.86, a: 1 }] },
  { key: "burst", label: "Burst", descriptor: "Click anywhere — SDF explosion with chromatic spray",
    gradient: [{ r: 0.98, g: 0.32, b: 0.18, a: 1 }, { r: 1.0, g: 0.78, b: 0.16, a: 1 }] },
  { key: "compose", label: "Compose", descriptor: "Click to stamp motifs. 5 types, draggable, persistent.",
    gradient: [{ r: 0.96, g: 0.55, b: 0.20, a: 1 }, { r: 0.40, g: 0.85, b: 0.95, a: 1 }] },
  { key: "audio", label: "Audio", descriptor: "Mic input — 32 frequency bins reactive via per-instance filters",
    gradient: [{ r: 0.32, g: 0.95, b: 0.65, a: 1 }, { r: 0.20, g: 0.45, b: 0.92, a: 1 }] },
  { key: "capture", label: "Capture", descriptor: "Click to download the current canvas as a real PNG.",
    gradient: [{ r: 0.96, g: 0.62, b: 0.20, a: 1 }, { r: 0.95, g: 0.42, b: 0.32, a: 1 }] },
];

// Compose-mode marker types — each click stamps one of these. Persistent.
type MarkerKind = "ghost" | "card" | "ring" | "spark" | "burst";
const MARKER_KINDS: MarkerKind[] = ["ghost", "card", "ring", "spark", "burst"];

export function sceneLab(w: number, h: number): Scene {
  const scene = createScene();
  let z = 0;
  const T = () => performance.now();
  const pad = 32;

  // ============================================================
  // MODE STATE — closure variable, mutates on tab tap.
  // ============================================================
  let currentMode: Mode = "chase";
  const setMode = (m: Mode) => { currentMode = m; };

  // ============================================================
  // POINTER STATE — fed in by main.ts each frame.
  // ============================================================
  let px = w / 2;
  let py = h / 2;
  let pDown = false;
  let pVx = 0;
  let pVy = 0;

  // Side door for main.ts to feed pointer state into this scene.
  (scene as unknown as { __labPointer?: (x: number, y: number, down: boolean) => void }).__labPointer =
    (x: number, y: number, down: boolean) => {
      pVx = x - px;
      pVy = y - py;
      px = x;
      py = y;
      pDown = down;
    };

  // Side door for main.ts to expose this scene's capture-mode PNG-download
  // hook to the engine frame loop. When main.ts sees __labCapture, it
  // publishes the function on window.__nsppCapture so the scene's tap
  // handler can call into it.
  (scene as unknown as { __labCapture?: (n: number) => void }).__labCapture =
    (n: number) => {
      // Convert the engine's visible canvas to a PNG blob and trigger a
      // download. The canvas is the one currently mounted on engine.canvas,
      // which is NOT the offscreen-mode canvas — it's the live one. We
      // rely on main.ts exposing window.__nsppCanvasEl with the right ref.
      const cap = (globalThis as unknown as { __nsppCanvasEl?: HTMLCanvasElement }).__nsppCanvasEl;
      if (!cap) return;
      try {
        const url = cap.toDataURL("image/png");
        const a = document.createElement("a");
        a.href = url;
        a.download = `nspp-capture-${n}.png`;
        a.click();
      } catch (e) {
        // CORS-tainted canvas: toDataURL throws a SecurityError. We
        // log but don't show errors to the user — the flash UI still
        // fires so they get feedback.
      }
    };

  // ============================================================
  // 1. BACKGROUND — diagonal gradient mesh
  // ============================================================
  addNode(scene, makeRect({
    x: 0, y: 0, w, h,
    fill: { r: 0.04, g: 0.04, b: 0.08, a: 1 },
    gradientEnd: { r: 0.18, g: 0.08, b: 0.32, a: 1 },
    gradientAngle: Math.PI / 3,
  })); z++;

  // ============================================================
  // 2. HEADER — title + current mode label
  // ============================================================
  addNode(scene, makeText({
    x: pad, y: 36, w: 400, h: 36,
    text: "NSPP · Lab",
    fontSize: 28,
    fontWeight: 800,
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    letterSpacing: -0.02,
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;

  // Mode descriptor — updates per frame so mode swaps feel live.
  const modeLabel = makeText({
    x: pad, y: 78, w: w - pad * 2, h: 22,
    text: MODES[0].descriptor,
    fontSize: 13,
    fontWeight: 500,
    textColor: { r: 1, g: 1, b: 1, a: 0.6 },
    fontFamily: "system-ui, -apple-system, sans-serif",
  });
  const updateModeLabel = (n: SceneNode) => {
    const spec = MODES.find((m) => m.key === currentMode)!;
    (n as unknown as { text: string }).text = spec.descriptor;
  };
  modeLabel.update = updateModeLabel;
  addNode(scene, modeLabel); z++;

  // ============================================================
  // 3a. CHASE — 12 ghost nodes that chase the pointer with per-node delay
  // ============================================================
  const GHOST_COUNT = 12;
  for (let i = 0; i < GHOST_COUNT; i++) {
    const angle = (i / GHOST_COUNT) * Math.PI * 2;
    const initR = 200;
    const ghost = makeRect({
      x: w / 2 + Math.cos(angle) * initR,
      y: h / 2 + Math.sin(angle) * initR,
      w: 36, h: 36,
      radius: 18,
      fill: { r: 0.98, g: 0.57, b: 0.24, a: 1 },
      gradientEnd: { r: 0.93, g: 0.28, b: 0.6, a: 1 },
      gradientAngle: Math.PI / 4,
      shadowBlur: 16, shadowOffsetY: 4,
      shadowColor: { r: 0.93, g: 0.28, b: 0.6, a: 0.6 },
      filter: { blur: 0, brightness: 1.1, contrast: 1, saturate: 1.4, hueRotate: 0, invert: 0 },
    });
    const ease = 0.08 - i * 0.003;
    ghost.update = (n) => {
      const isActive = currentMode === "chase";
      n.opacity = isActive ? 1 : 0;
      if (!isActive) return;
      // Target = pointer + small offset (creates orbit around cursor)
      const target = px + Math.cos(T() / 800 + i) * 30;
      const targetY = py + Math.sin(T() / 800 + i) * 30;
      // Lerp toward target — per-node ease makes a chase
      n.x += (target - n.x) * Math.max(0.02, ease);
      n.y += (targetY - n.y) * Math.max(0.02, ease);
      // Bloom when close to pointer
      const dx = n.x + 18 - px;
      const dy = n.y + 18 - py;
      const dist = Math.hypot(dx, dy);
      const closeness = Math.max(0, 1 - dist / 200);
      n.scaleX = n.scaleY = 1 + closeness * 0.4;
      const f: Filter = { blur: 0, brightness: 1 + closeness * 0.5, contrast: 1, saturate: 1.4 + closeness * 0.6, hueRotate: 0, invert: 0 };
      n.filter = f;
    };
    addNode(scene, ghost); z++;
  }

  // ============================================================
  // 3b. PAINT — ring buffer of stroke segments
  // ============================================================
  const PAINT_MAX = 60;
  const paintHead = { i: 0 };
  // Stash last paint point + velocity on this closure object
  const paintState = { lastX: -1000, lastY: -1000, speed: 0 };
  // Pre-allocate segments off-screen
  const paintSegments: SceneNode[] = [];
  for (let i = 0; i < PAINT_MAX; i++) {
    const seg = makeRect({
      x: -100, y: -100, w: 12, h: 12,
      radius: 6,
      fill: { r: 0.2, g: 0.83, b: 0.6, a: 1 },
      gradientEnd: { r: 0.02, g: 0.71, b: 0.83, a: 1 },
      gradientAngle: Math.PI / 4,
    });
    // Store age (0 = newest) in scaleX so subsequent frames can read it.
    seg.update = (n) => {
      const isActive = currentMode === "paint";
      if (!isActive) { n.opacity = 0; return; }
      const segIdx = (n as unknown as { __paintIdx: number }).__paintIdx;
      const age = (paintHead.i - segIdx - 1 + PAINT_MAX) % PAINT_MAX;
      if (age >= PAINT_MAX - 1) { n.opacity = 0; return; }
      n.opacity = Math.max(0, 1 - age / (PAINT_MAX - 1));
      // Color by velocity at sample time (stored in n.prevX / prevY as
      // a sneaky side channel — they're not used here otherwise)
      const vx = (n as unknown as { __paintVx: number }).__paintVx ?? 0;
      const vy = (n as unknown as { __paintVy: number }).__paintVy ?? 0;
      const speed = Math.hypot(vx, vy);
      const hot = Math.min(1, speed / 30);
      n.fill = { r: 0.2 + hot * 0.78, g: 0.83 - hot * 0.26, b: 0.6 - hot * 0.32, a: 1 };
      // Size scales with velocity — chunky when slow, fat when fast
      const scale = 0.6 + Math.min(1.8, speed / 12);
      n.w = 12 * scale;
      n.h = 12 * scale;
      n.filter = { blur: 0, brightness: 1 + hot * 0.3, contrast: 1, saturate: 1.3 + hot * 0.5, hueRotate: 0, invert: 0 };
    };
    (seg as unknown as { __paintIdx: number }).__paintIdx = i;
    paintSegments.push(seg);
    addNode(scene, seg); z++;
  }
  // The first segment's update runs each frame — use it to advance the
  // ring buffer (since updates are called per node per frame, picking
  // segment 0 as the "tick" is fine — we know segment 0's update runs
  // every frame because it's iterated like any other).
  paintSegments[0].update = ((origUpdate) => (n: SceneNode) => {
    origUpdate(n);
    if (currentMode !== "paint") return;
    // Append current pointer pos to ring buffer if pointer is moving
    if (!pDown) {
      paintState.speed = 0;
      return;
    }
    paintState.speed = Math.hypot(pVx, pVy);
    // Always append when pointer is down — even at rest, the user is
    // "painting" so we want to capture every frame's position.
    const slot = paintSegments[paintHead.i];
    slot.x = px - 6;
    slot.y = py - 6;
    (slot as unknown as { __paintVx: number }).__paintVx = pVx;
    (slot as unknown as { __paintVy: number }).__paintVy = pVy;
    paintHead.i = (paintHead.i + 1) % PAINT_MAX;
    void paintState;
  })(paintSegments[0].update!);

  // ============================================================
  // 3c. ORBIT — 24 particles with SDF gravity toward pointer
  // ============================================================
  const PARTICLE_COUNT = 24;
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const angle = (i / PARTICLE_COUNT) * Math.PI * 2;
    const r = 200 + (i * 13) % 100; // pseudo-random radius
    const px0 = w / 2 + Math.cos(angle) * r;
    const py0 = h / 2 + Math.sin(angle) * r;
    const p = makeRect({
      x: px0, y: py0, w: 14, h: 14,
      radius: 7,
      fill: { r: 0.65, g: 0.55, b: 0.98, a: 1 },
      gradientEnd: { r: 0.93, g: 0.28, b: 0.6, a: 1 },
      gradientAngle: Math.PI / 4,
    });
    const phase = i * 0.4;
    p.update = (n) => {
      const isActive = currentMode === "orbit";
      n.opacity = isActive ? 1 : 0;
      if (!isActive) return;
      const dx = px - (n.x + 7);
      const dy = py - (n.y + 7);
      const dist = Math.hypot(dx, dy);
      // Pull toward pointer (inverse distance, clamped)
      const pull = Math.min(2, 80 / Math.max(40, dist));
      const targetX = n.x + dx * 0.012 * pull + Math.cos(T() / 1500 + phase) * 0.6;
      const targetY = n.y + dy * 0.012 * pull + Math.sin(T() / 1500 + phase) * 0.6;
      n.x += (targetX - n.x) * 0.12;
      n.y += (targetY - n.y) * 0.12;
      // Saturate when close
      const closeness = Math.max(0, 1 - dist / 200);
      n.filter = { blur: 0, brightness: 1 + closeness * 0.4, contrast: 1, saturate: 1.4 + closeness * 0.6, hueRotate: 0, invert: 0 };
      n.scaleX = n.scaleY = 1 + closeness * 0.3;
    };
    addNode(scene, p); z++;
  }

  // ============================================================
  // 3d. REEL — 8 cards arranged horizontally with per-card filter chain.
  //     Interactive: click+drag the canvas to slide the reel. On release
  //     the reel keeps coasting with friction (real inertia).
  // ============================================================
  const REEL_COUNT = 8;
  const cardW = 220;
  const cardH = 280;
  const cardGap = 20;
  const reelY = (h - cardH) / 2 - 30;
  const reelX0 = (w - cardW) / 2;
  const cardLabels = ["Drag", "Velocity", "Glow", "Bounce", "Slide", "Pulse", "Float", "Spin"];

  // Reel scroll offset — accumulated from pointer drag, decays via friction
  // when the user releases. Cards reposition based on this single value.
  const reel = {
    offset: 0,       // current scroll position in CSS px
    velocity: 0,     // px/sec, decays each frame when idle
    wasDown: false,  // tracks pointer down → up transitions
  };

  for (let i = 0; i < REEL_COUNT; i++) {
    const baseX = reelX0 + i * (cardW + cardGap);
    const hue = (i / REEL_COUNT) * 360;
    const card = makeRect({
      x: baseX, y: reelY, w: cardW, h: cardH,
      radius: 20,
      fill: { r: 0.15, g: 0.12, b: 0.22, a: 1 },
      borderWidths: [1, 1, 1, 1],
      borderColor: { r: 1, g: 1, b: 1, a: 0.16 },
      shadowOffsetY: 8, shadowBlur: 24,
      shadowColor: { r: 0, g: 0, b: 0, a: 0.5 },
      filter: { blur: 0, brightness: 1, contrast: 1.05, saturate: 1.3, hueRotate: hue, invert: 0 },
    });
    card.update = (n) => {
      if (currentMode !== "reel") { n.opacity = 0; return; }
      n.opacity = 1;
      // Apply scroll offset — the whole reel moves as a group
      n.x = baseX + reel.offset;
      // Subtle scale on the center card (closest to viewport center) for
      // a "focus" feel as you scroll.
      const cardCenter = n.x + cardW / 2;
      const dist = Math.abs(cardCenter - w / 2);
      const focus = Math.max(0, 1 - dist / (cardW * 2));
      n.scaleX = n.scaleY = 1 + focus * 0.08;
      n.opacity = 0.4 + 0.6 * focus;
    };
    addNode(scene, card); z++;
    // Card number
    const num = makeText({
      x: baseX, y: reelY + 24, w: cardW, h: 44,
      text: `${i + 1}`,
      fontSize: 40,
      fontWeight: 800,
      textColor: { r: 1, g: 1, b: 1, a: 0.9 },
      textAlign: "center",
      letterSpacing: -0.02,
      fontFamily: "system-ui, -apple-system, sans-serif",
    });
    num.update = (n) => {
      if (currentMode !== "reel") { n.opacity = 0; return; }
      n.opacity = 1;
      n.x = baseX + reel.offset;
    };
    addNode(scene, num); z++;
    // Card label
    const title = makeText({
      x: baseX, y: reelY + cardH - 48, w: cardW, h: 22,
      text: cardLabels[i],
      fontSize: 14,
      fontWeight: 700,
      textColor: { r: 1, g: 1, b: 1, a: 0.85 },
      textAlign: "center",
      letterSpacing: 0.04,
      fontFamily: "system-ui, -apple-system, sans-serif",
    });
    title.update = (n) => {
      if (currentMode !== "reel") { n.opacity = 0; return; }
      n.opacity = 1;
      n.x = baseX + reel.offset;
    };
    addNode(scene, title); z++;
  }

  // Reel inertia tick — runs from a "tick" node's update() so it's only
  // invoked when the reel is visible. Avoids interfering with other modes.
  const reelTick = makeRect({
    x: -100, y: -100, w: 1, h: 1, opacity: 0,
    fill: { r: 0, g: 0, b: 0, a: 0 },
  });
  reelTick.update = () => {
    if (currentMode !== "reel") {
      reel.velocity = 0;
      return;
    }
    // If pointer is down, accumulate offset from horizontal velocity.
    // pVx is set per-frame by __labPointer in CSS px.
    if (pDown) {
      reel.offset += pVx;
      reel.velocity = pVx * 60; // px/sec estimate (pVx is per-frame)
      reel.wasDown = true;
    } else if (reel.wasDown) {
      // Just released — commit the last velocity, then decay.
      reel.wasDown = false;
    } else if (reel.velocity !== 0) {
      // Friction decay (~92% per frame at 60fps → ~3% remaining after 1s)
      reel.velocity *= 0.92;
      reel.offset += reel.velocity / 60;
      if (Math.abs(reel.velocity) < 8) reel.velocity = 0;
    }
    // Soft clamp so the reel can't drift forever. After 5 cards of
    // overshoot the user has clearly scrolled past the end.
    const minOff = -(REEL_COUNT - 1) * (cardW + cardGap);
    const maxOff = cardW + cardGap;
    if (reel.offset < minOff) {
      reel.offset = minOff + (reel.offset - minOff) * 0.5;
      reel.velocity = 0;
    } else if (reel.offset > maxOff) {
      reel.offset = maxOff + (reel.offset - maxOff) * 0.5;
      reel.velocity = 0;
    }
  };
  addNode(scene, reelTick); z++;

  // ============================================================
  // 3e. VIDEO WALL — 64-tile live mosaic of a video file the user drops.
  //     Per-frame: copy current <video> frame to a 2D canvas, upload that
  //     canvas to 64 separate SDF rect textures (one per tile), each with
  //     a different per-instance hue rotation. The result: a 60 FPS mosaic
  //     that LOOKS like a single image but is actually 64 independently-
  //     filtered rects. This is the headline demo.
  // ============================================================
  const VIDEO_COLS = 8;
  const VIDEO_ROWS = 8;
  const VIDEO_MARGIN = 80;
  const VIDEO_TOP = 140;
  const videoTileW = (w - VIDEO_MARGIN * 2) / VIDEO_COLS;
  const videoTileH = Math.min((h - VIDEO_TOP - 140) / VIDEO_ROWS, videoTileW);

  // Hidden <video> element — lives off-screen, fed by a drop or file picker.
  const videoEl: HTMLVideoElement | null = (() => {
    if (typeof document === "undefined") return null;
    const v = document.createElement("video");
    v.style.position = "fixed";
    v.style.top = "-9999px";
    v.style.left = "-9999px";
    v.style.width = "1px";
    v.style.height = "1px";
    v.muted = true;
    v.playsInline = true;
    v.loop = true;
    v.crossOrigin = "anonymous";
    document.body.appendChild(v);
    return v;
  })();

  // Shared scratch canvas for the per-frame draw — 320×180 keeps upload
  // bandwidth low while still being detailed enough to look crisp at tile
  // sizes around 100-160px CSS.
  const videoCanvas: HTMLCanvasElement | null = (() => {
    if (typeof document === "undefined") return null;
    const c = document.createElement("canvas");
    c.width = 320;
    c.height = 180;
    return c;
  })();
  const videoCtx = videoCanvas?.getContext("2d") ?? null;

  // Track whether we have a video frame ready to display.
  let videoReady = false;
  let videoFileName = "";

  // Build the 64 tile nodes. Each carries a unique hue rotation so the
  // mosaic has chromatic variation per tile. They become visible when
  // VIDEO WALL mode is active.
  const videoTiles: SceneNode[] = [];
  for (let r = 0; r < VIDEO_ROWS; r++) {
    for (let c = 0; c < VIDEO_COLS; c++) {
      const idx = r * VIDEO_COLS + c;
      const tileX = VIDEO_MARGIN + c * videoTileW + 2;
      const tileY = VIDEO_TOP + r * videoTileH + 2;
      const tileW = videoTileW - 4;
      const tileH = videoTileH - 4;
      const hue = ((idx * 37) % 360); // pseudo-random per-tile hue
      const tile = makeRect({
        x: tileX, y: tileY, w: tileW, h: tileH,
        radius: 4,
        fill: { r: 0.08, g: 0.08, b: 0.12, a: 1 },
        filter: {
          blur: 0, brightness: 1, contrast: 1.05, saturate: 1.3,
          hueRotate: hue, invert: 0,
        },
      });
      tile.update = (n) => {
        n.opacity = currentMode === "video" && videoReady ? 1 : 0;
      };
      videoTiles.push(tile);
      addNode(scene, tile); z++;
    }
  }

  // Placeholder text shown when no video has been dropped yet.
  const videoPrompt = makeText({
    x: VIDEO_MARGIN, y: VIDEO_TOP + (videoTileH * VIDEO_ROWS) / 2 - 16,
    w: w - VIDEO_MARGIN * 2, h: 32,
    text: "Drop an MP4 here, or click to pick a file",
    fontSize: 18, fontWeight: 600,
    textAlign: "center",
    textColor: { r: 1, g: 1, b: 1, a: 0.7 },
    fontFamily: "system-ui, -apple-system, sans-serif",
  });
  videoPrompt.update = (n) => {
    n.opacity = currentMode === "video" && !videoReady ? 1 : 0;
  };
  addNode(scene, videoPrompt); z++;

  // Filename caption (top-left of the wall)
  const videoCaption = makeText({
    x: VIDEO_MARGIN, y: VIDEO_TOP - 32,
    w: w - VIDEO_MARGIN * 2, h: 18,
    text: "",
    fontSize: 12, fontWeight: 700,
    letterSpacing: 2,
    textColor: { r: 1, g: 1, b: 1, a: 0.6 },
    fontFamily: "system-ui, -apple-system, sans-serif",
  });
  videoCaption.update = (n) => {
    if (currentMode !== "video" || !videoReady) {
      n.opacity = 0;
      return;
    }
    n.opacity = 1;
    (n as unknown as { text: string }).text = `▶ ${videoFileName}`;
  };
  addNode(scene, videoCaption); z++;

  // Hidden file input — clicking the prompt opens the picker.
  const videoInput: HTMLInputElement | null = (() => {
    if (typeof document === "undefined") return null;
    const i = document.createElement("input");
    i.type = "file";
    i.accept = "video/mp4,video/webm,video/*";
    i.style.position = "fixed";
    i.style.top = "-9999px";
    i.style.left = "-9999px";
    document.body.appendChild(i);
    i.addEventListener("change", () => {
      const file = i.files?.[0];
      if (!file || !videoEl) return;
      videoFileName = file.name;
      const url = URL.createObjectURL(file);
      videoEl.src = url;
      videoEl.load();
      videoEl.play().catch(() => { /* user gesture may be required */ });
      videoReady = false;
      // First frame readiness: when video has dimensions, mark ready.
      const onReady = () => {
        if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
          videoReady = true;
          videoEl.removeEventListener("loadeddata", onReady);
        }
      };
      videoEl.addEventListener("loadeddata", onReady);
    });
    return i;
  })();

  // Click on the prompt opens the file picker (works on touch + desktop).
  videoPrompt.onTap = () => { videoInput?.click(); };

  // Drag-and-drop onto the canvas — drops the file onto the hidden video.
  const handleDragOver = (e: DragEvent) => { e.preventDefault(); };
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (!file || !videoEl) return;
    if (!file.type.startsWith("video/")) return;
    videoFileName = file.name;
    const url = URL.createObjectURL(file);
    videoEl.src = url;
    videoEl.load();
    videoEl.play().catch(() => {});
    videoReady = false;
    const onReady = () => {
      if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
        videoReady = true;
        videoEl.removeEventListener("loadeddata", onReady);
      }
    };
    videoEl.addEventListener("loadeddata", onReady);
  };
  if (typeof document !== "undefined") {
    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("drop", handleDrop);
  }

  // Per-frame scene tick — copies the current video frame into the shared
  // canvas, then uploads that canvas to each tile's GPU texture.
  (scene as unknown as {
    __labTick?: (gl: WebGL2RenderingContext, _t: number, _dt: number) => void;
  }).__labTick = (gl: WebGL2RenderingContext) => {
    if (currentMode !== "video" || !videoReady || !videoEl || !videoCanvas || !videoCtx) return;
    if (videoEl.readyState < 2) return; // HAVE_CURRENT_DATA
    // Draw the current video frame into the scratch canvas.
    videoCtx.drawImage(videoEl, 0, 0, videoCanvas.width, videoCanvas.height);
    // Upload to each tile's texture. Imported lazily so this stays optional.
    import("../imageTexture").then(({ uploadLiveCanvasTexture }) => {
      for (const tile of videoTiles) {
        uploadLiveCanvasTexture(
          gl, tile.id, videoCanvas, videoTileW, videoTileH,
        );
      }
    }).catch(() => { /* imageTexture unavailable */ });
  };

  // ============================================================
  // 3f. BURST — click anywhere on the canvas → 80 SDF particles erupt
  //     outward from the pointer with per-particle velocity, hue,
  //     and size. They arc back toward the cursor with friction.
  //     The motion-blur postprocess naturally smears each particle's
  //     trajectory into a streak — that's the headline effect.
  // ============================================================
  const BURST_PARTICLES = 80;
  // Pre-allocate all particles. Each one stays at (-1000,-1000) until
  // activated by a burst event, then runs its own life cycle.
  const burstState: Array<{
    x: number; y: number;
    vx: number; vy: number;
    life: number;     // 1.0 = newborn, 0 = dead
    hue: number;
  }> = [];
  for (let i = 0; i < BURST_PARTICLES; i++) {
    burstState.push({ x: -1000, y: -1000, vx: 0, vy: 0, life: 0, hue: 0 });
    const p = makeRect({
      x: -1000, y: -1000, w: 18, h: 18, radius: 9,
      fill: { r: 1, g: 0.7, b: 0.2, a: 1 },
      filter: { blur: 0, brightness: 1.2, contrast: 1, saturate: 1.5, hueRotate: 0, invert: 0 },
      shadowBlur: 12, shadowOffsetY: 0,
      shadowColor: { r: 1, g: 0.5, b: 0.1, a: 0.6 },
    });
    p.update = (n) => {
      const ps = burstState[i]!;
      // Visible only in burst mode AND alive
      n.opacity = currentMode === "burst" && ps.life > 0 ? Math.min(1, ps.life * 2) : 0;
      if (ps.life <= 0) return;
      n.x = ps.x - 9;
      n.y = ps.y - 9;
      // Apply velocity
      ps.x += ps.vx;
      ps.y += ps.vy;
      // Gravity-ish arc back toward pointer — but only a slight pull,
      // so particles fly outward in a visible spray first.
      const dx = px - ps.x;
      const dy = py - ps.y;
      const dist = Math.max(40, Math.hypot(dx, dy));
      ps.vx += (dx / dist) * 0.35;
      ps.vy += (dy / dist) * 0.35;
      // Friction (slows the spray over ~1.5s)
      ps.vx *= 0.965;
      ps.vy *= 0.965;
      // Shrink as life decays
      const s = 1 + ps.life * 0.5;
      n.w = 18 * s;
      n.h = 18 * s;
      // Per-instance filter — hue + saturate driven by per-particle data
      n.filter = {
        blur: 0, brightness: 1.2 + (1 - ps.life) * 0.4,
        contrast: 1, saturate: 1.4 + ps.life * 0.8,
        hueRotate: ps.hue, invert: 0,
      };
      // Decay
      ps.life -= 0.008;
      if (ps.life <= 0) {
        ps.x = -1000; ps.y = -1000;
      }
    };
    addNode(scene, p); z++;
  }
  // Spawn a burst at the current pointer position. Called from __labPointer
  // when pointer-down transitions occur in burst mode.
  const spawnBurst = (cx: number, cy: number) => {
    let spawned = 0;
    for (let i = 0; i < BURST_PARTICLES && spawned < 60; i++) {
      const ps = burstState[i]!;
      if (ps.life > 0.05) continue;
      const angle = Math.random() * Math.PI * 2;
      const speed = 6 + Math.random() * 14;
      ps.x = cx;
      ps.y = cy;
      ps.vx = Math.cos(angle) * speed;
      ps.vy = Math.sin(angle) * speed;
      ps.life = 1.0;
      ps.hue = (Math.random() - 0.5) * 240; // -120 to +120 → spans red→green→blue
      spawned++;
    }
  };
  // Track pointer-down edge to trigger a burst
  let wasDownLastTick = false;
  const burstTick = makeRect({
    x: -100, y: -100, w: 1, h: 1, opacity: 0,
    fill: { r: 0, g: 0, b: 0, a: 0 },
  });
  burstTick.update = () => {
    if (currentMode !== "burst") {
      wasDownLastTick = false;
      return;
    }
    // Edge-triggered: pointer JUST went down → burst at pointer position
    if (pDown && !wasDownLastTick) {
      spawnBurst(px, py);
    }
    wasDownLastTick = pDown;
  };
  addNode(scene, burstTick); z++;

  // ============================================================
  // 3g. COMPOSE — freestyle canvas. Click to stamp motifs. Persistent.
  //     5 motif types cycle by default; click the floating palette to
  //     pick one. Markers are draggable. Click "CLEAR" to wipe all.
  // ============================================================
  type Marker = {
    kind: MarkerKind;
    node: SceneNode;
    ring?: SceneNode;
    particles?: SceneNode[]; // for bursts that fade
    bornAt: number;          // performance.now() ms
    hueShift: number;        // unique hue for per-instance filter
    home: { x: number; y: number }; // resting position
    drag?: { offX: number; offY: number };
  };
  let markers: Marker[] = [];
  let activeKind: MarkerKind = "ghost";
  let activeKindIdx = 0;
  let clickCount = 0;

  // Helper: spawn a single marker at (x, y) of the given kind.
  const spawnMarker = (kind: MarkerKind, x: number, y: number): Marker => {
    const baseHueShift = (markers.length * 47) % 360;
    clickCount++;
    const unique = baseHueShift + clickCount * 13;
    let node: SceneNode;
    let ring: SceneNode | undefined;
    let particles: SceneNode[] | [];

    if (kind === "ghost") {
      // Round gradient orb with subtle breathing — the chase-ghost archetype.
      const sz = 56 + Math.random() * 16;
      node = makeRect({
        x: x - sz / 2, y: y - sz / 2, w: sz, h: sz,
        radius: sz / 2,
        fill: { r: 0.98, g: 0.62, b: 0.32, a: 0.95 },
        gradientEnd: { r: 0.93, g: 0.28, b: 0.60, a: 1 },
        gradientAngle: Math.PI / 4 + (unique % 7) * 0.1,
        filter: { blur: 0, brightness: 1.05, contrast: 1.1, saturate: 1.3, hueRotate: (unique * Math.PI / 180) * 0.3, invert: 0 },
      });
    } else if (kind === "card") {
      // Stroke rectangle with stripe gradient (the reel-archetype).
      const w = 92 + Math.random() * 24, h = 56 + Math.random() * 16;
      node = makeRect({
        x: x - w / 2, y: y - h / 2, w, h,
        radius: 4,
        fill: { r: 0.14, g: 0.10, b: 0.18, a: 0.95 },
        gradientEnd: { r: 0.96, g: 0.32, b: 0.45, a: 1 },
        gradientAngle: 0.6 + (unique % 5) * 0.15,
        filter: { blur: 0, brightness: 1, contrast: 1, saturate: 1.4, hueRotate: (unique * Math.PI / 180) * 0.4, invert: 0 },
      });
    } else if (kind === "ring") {
      // Hollow ring — drawn as a small filled circle with a same-color
      // "hole" rect on top. Outer radius > 0, inner radius = the hole.
      const outer = 50 + Math.random() * 18;
      const inner = outer * 0.7;
      node = makeRect({
        x: x - outer / 2, y: y - outer / 2, w: outer, h: outer,
        radius: outer / 2,
        fill: { r: 0.40, g: 0.85, b: 0.95, a: 1 },
        gradientEnd: { r: 0.65, g: 0.32, b: 0.96, a: 1 },
        gradientAngle: Math.PI / 2 + (unique % 3) * 0.2,
        filter: { blur: 0, brightness: 1, contrast: 1, saturate: 1.3, hueRotate: (unique * Math.PI / 180) * 0.2, invert: 0 },
      });
      // Inner "hole" — same color as the bg, slightly smaller, perfectly
      // centered. Together with the outer disc this renders as a ring.
      ring = makeRect({
        x: x - inner / 2, y: y - inner / 2, w: inner, h: inner,
        radius: inner / 2,
        fill: { r: 0.025, g: 0.025, b: 0.06, a: 1 },
      });
      addNode(scene, ring);
    } else if (kind === "spark") {
      // Diamond-ish accent — small gradient diamond.
      const sz = 36 + Math.random() * 12;
      node = makeRect({
        x: x - sz / 2, y: y - sz / 2, w: sz, h: sz,
        radius: 4,
        fill: { r: 0.98, g: 0.86, b: 0.18, a: 1 },
        gradientEnd: { r: 0.96, g: 0.42, b: 0.18, a: 1 },
        gradientAngle: 0.4 + (unique % 5) * 0.1,
        rotation: Math.PI / 4,
        transformOriginX: 0.5, transformOriginY: 0.5,
        filter: { blur: 0, brightness: 1.1, contrast: 1.15, saturate: 1.5, hueRotate: (unique * Math.PI / 180) * 0.5, invert: 0 },
      });
    } else {
      // "burst" — fade-out debris burst. 8 particles, each with its own
      // velocity and color, all live for ~1.5s.
      node = makeRect({
        x, y, w: 4, h: 4,
        radius: 2,
        fill: { r: 0.98, g: 0.6, b: 0.18, a: 1 },
        opacity: 0,
      });
      particles = [];
      for (let i = 0; i < 10; i++) {
        const p = makeRect({
          x: x - 2, y: y - 2, w: 6, h: 6,
          radius: 3,
          fill: { r: 0.98, g: 0.42, b: 0.18, a: 1 },
        });
        addNode(scene, p);
        particles.push(p);
      }
    }
    addNode(scene, node);
    return {
      kind, node, ring, particles,
      bornAt: performance.now(),
      hueShift: unique,
      home: { x, y },
    };
  };

  // Pre-allocate a small palette of 25 markers so the scene can hold
  // up to 25 simultaneously. Older markers get reaped at the cap.
  const MAX_MARKERS = 25;
  const reapOldest = () => {
    while (markers.length >= MAX_MARKERS) {
      const oldest = markers.shift()!;
      // We can't actually remove SceneNodes from the scene graph, so
      // we just hide them by moving them to (-9999,-9999) and setting
      // their opacity to 0. Visually invisible.
      oldest.node.x = -9999; oldest.node.y = -9999;
      oldest.node.w = 0; oldest.node.h = 0; oldest.node.opacity = 0;
      if (oldest.ring) { oldest.ring.x = -9999; oldest.ring.y = -9999; oldest.ring.w = 0; oldest.ring.h = 0; oldest.ring.opacity = 0; }
      if (oldest.particles) for (const p of oldest.particles) { p.x = -9999; p.y = -9999; p.opacity = 0; }
    }
  };

  // Compose-mode update tick — drives all markers' breathing/animation.
  const composeRoot = makeRect({
    x: -10, y: -10, w: 1, h: 1, opacity: 0,
    fill: { r: 0, g: 0, b: 0, a: 0 },
  });
  composeRoot.update = () => {
    if (currentMode !== "compose") return;
    const t = performance.now();
    for (const m of markers) {
      if (m.kind === "burst") {
        // Burst markers are 10 debris particles that fly out and fade.
        const age = (t - m.bornAt) / 1000;
        if (age > 1.5) continue; // particles still exist on canvas but invisible (w=0)
        const fade = Math.max(0, 1 - age / 1.5);
        for (let i = 0; i < (m.particles?.length ?? 0); i++) {
          const p = m.particles![i]!;
          const angle = (i / 10) * Math.PI * 2;
          const radius = age * (60 + (i % 4) * 18);
          p.x = m.home.x + Math.cos(angle) * radius;
          p.y = m.home.y + Math.sin(angle) * radius;
          p.opacity = fade;
          if (p.fill) p.fill.a = fade;
        }
      } else if (m.kind === "ghost") {
        // Subtle breathing on the radius (via scale up/down).
        const breath = 1 + Math.sin((t - m.bornAt) / 800) * 0.04;
        if (m.node.scaleX !== undefined) {
          m.node.scaleX = breath; m.node.scaleY = breath;
        }
      } else if (m.kind === "ring" || m.kind === "card") {
        // Gentle rotation for visual life
        if (m.node.rotation !== undefined) {
          m.node.rotation = (t - m.bornAt) / 4000;
        }
      } else if (m.kind === "spark") {
        // Spin fast
        if (m.node.rotation !== undefined) {
          m.node.rotation = (t - m.bornAt) / 1200;
        }
      }
    }
  };
  addNode(scene, composeRoot);
  // Hidden — but the update() closure is what matters, not the rect.

  // Click handler — spawns a marker. Also drives the marker-kind cycle.
  const composeClick = makeRect({
    x: -10, y: -10, w: 1, h: 1, opacity: 0,
    fill: { r: 0, g: 0, b: 0, a: 0 },
  });
  let wasDownCompose = false;
  composeClick.update = () => {
    if (currentMode !== "compose") {
      wasDownCompose = false;
      return;
    }
    if (pDown && !wasDownCompose) {
      // Spawn at pointer. Cycle through kinds.
      const kind = MARKER_KINDS[activeKindIdx % MARKER_KINDS.length]!;
      activeKindIdx = (activeKindIdx + 1) % MARKER_KINDS.length;
      const m = spawnMarker(kind, px, py);
      markers.push(m);
      reapOldest();
    }
    wasDownCompose = pDown;
  };
  addNode(scene, composeClick);

  // Floating "marker kind" palette — top-right of canvas, hover labels.
  const paletteW = 64, paletteH = 28, paletteGap = 6;
  const paletteX = w - pad - paletteW * MARKER_KINDS.length - paletteGap * (MARKER_KINDS.length - 1);
  const paletteY = pad;
  const paletteHits: SceneNode[] = [];
  for (let i = 0; i < MARKER_KINDS.length; i++) {
    const kind = MARKER_KINDS[i]!;
    const x = paletteX + i * (paletteW + paletteGap);
    const hit = makeRect({
      x, y: paletteY, w: paletteW, h: paletteH,
      radius: 14,
      fill: { r: 1, g: 1, b: 1, a: 0 },
      borderWidths: [1, 1, 1, 1],
      borderColor: { r: 1, g: 1, b: 1, a: 0.30 },
    });
    hit.onTap = () => {
      activeKindIdx = MARKER_KINDS.indexOf(kind);
      activeKind = kind;
    };
    hit.update = (n) => {
      const isActive = activeKindIdx === MARKER_KINDS.indexOf(kind);
      n.fill = isActive ? { r: 0.96, g: 0.55, b: 0.20, a: 1 } : { r: 1, g: 1, b: 1, a: 0 };
      n.borderColor = isActive
        ? { r: 0.96, g: 0.55, b: 0.20, a: 0.95 }
        : { r: 1, g: 1, b: 1, a: 0.30 };
    };
    addNode(scene, hit);
    paletteHits.push(hit);
    addNode(scene, makeText({
      x, y: paletteY, w: paletteW, h: paletteH,
      text: kind.toUpperCase(),
      fontSize: 9,
      fontWeight: 700,
      textColor: { r: 1, g: 1, b: 1, a: 0.85 },
      textAlign: "center",
      letterSpacing: 0.16,
      fontFamily: "system-ui, -apple-system, sans-serif",
    })); z++;
  }

  // CLEAR button — bottom-left of canvas, just above the mode tab bar.
  const clearX = pad;
  const clearY = h - 80 - 56 - 12;
  const clearW = 88, clearH = 32;
  const clearBtn = makeRect({
    x: clearX, y: clearY, w: clearW, h: clearH,
    radius: 16,
    fill: { r: 1, g: 1, b: 1, a: 0 },
    borderWidths: [1, 1, 1, 1],
    borderColor: { r: 1, g: 1, b: 1, a: 0.30 },
  });
  clearBtn.onTap = () => {
    for (const m of markers) {
      m.node.x = -9999; m.node.y = -9999;
      m.node.w = 0; m.node.h = 0; m.node.opacity = 0;
      if (m.ring) { m.ring.x = -9999; m.ring.y = -9999; m.ring.w = 0; m.ring.h = 0; m.ring.opacity = 0; }
      if (m.particles) for (const p of m.particles) { p.x = -9999; p.y = -9999; p.opacity = 0; }
    }
    markers.length = 0;
  };
  clearBtn.update = (n) => {
    n.fill = { r: 0.96, g: 0.32, b: 0.18, a: 0.18 };
    n.borderColor = { r: 0.96, g: 0.32, b: 0.18, a: 0.6 };
  };
  addNode(scene, clearBtn);
  addNode(scene, makeText({
    x: clearX, y: clearY, w: clearW, h: clearH,
    text: "CLEAR",
    fontSize: 11,
    fontWeight: 700,
    textColor: { r: 1, g: 1, b: 1, a: 0.95 },
    textAlign: "center",
    letterSpacing: 0.20,
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;

  // Markers counter chip — top-left of canvas, near the NSPP·Lab title
  const countChipX = pad + 220;
  const countChipY = pad;
  const countChip = makeText({
    x: countChipX, y: countChipY, w: 140, h: 14,
    text: "0 MARKS",
    fontSize: 10,
    fontWeight: 700,
    textColor: { r: 1, g: 1, b: 1, a: 0.55 },
    letterSpacing: 0.30,
    fontFamily: "system-ui, -apple-system, sans-serif",
  });
  addNode(scene, countChip);
  countChip.update = (n) => {
    if (currentMode === "compose") {
      n.text = `${markers.length} / ${MAX_MARKERS} MARKS`;
    } else {
      n.text = `${markers.length} MARKS`;
    }
  };

  // ============================================================
  // 3h. AUDIO — Web Audio AnalyserNode → 32 frequency bins → per-instance
  //     reactive bar chart. Click anywhere in audio mode to start/stop
  //     mic capture (the browser prompts for permission). Click again to
  //     stop. Bars are colored by frequency band: bass = warm, mids =
  //     neutral, treble = cool.
  // ============================================================
  // Audio state lives outside the scene's reusable scope via the closure.
  let audioCtx: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let micStream: MediaStream | null = null;
  let audioStarted = false;
  let audioRequested = false;

  // Pre-allocate 32 bars (frequency bins). Each bar is a Node that has
  // a per-instance filter chain reactive to its band. Persistent node set.
  const AUDIO_BINS = 32;
  const audioBars: SceneNode[] = [];
  for (let i = 0; i < AUDIO_BINS; i++) {
    const x = pad + i * ((w - pad * 2) / AUDIO_BINS);
    const y = h - 200;
    const bar = makeRect({
      x, y,
      w: (w - pad * 2) / AUDIO_BINS * 0.7,
      h: 4,
      radius: 2,
      fill: { r: 0.32, g: 0.95, b: 0.65, a: 0.8 },
      gradientEnd: { r: 0.20, g: 0.45, b: 0.92, a: 1 },
      gradientAngle: Math.PI / 2,
      filter: { blur: 0, brightness: 1, contrast: 1, saturate: 1.4, hueRotate: 0, invert: 0 },
    });
    addNode(scene, bar);
    audioBars.push(bar);
  }
  // Audio "share the bus" — a single tick node drives all 32 bars from
  // the same FFT read.
  const audioTick = makeRect({
    x: -10, y: -10, w: 1, h: 1, opacity: 0,
    fill: { r: 0, g: 0, b: 0, a: 0 },
  });
  const audioBuf = new Uint8Array(2048);
  audioTick.update = () => {
    if (currentMode !== "audio") return;

    if (audioRequested && !audioStarted) {
      // Toggle: click in audio mode = toggle audio capture. The very first
      // click in audio mode STARTS audio; subsequent click STOPS it.
      audioStarted = true;
      try {
        const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
        if (Ctor) {
          audioCtx = new Ctor();
          navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
            micStream = stream;
            const src = audioCtx!.createMediaStreamSource(stream);
            analyser = audioCtx!.createAnalyser();
            analyser.fftSize = 2048;
            analyser.smoothingTimeConstant = 0.7;
            src.connect(analyser);
          }).catch(() => {
            // User denied or no mic — fall back to synth (oscillator)
            // so the demo is still visible.
            const osc = audioCtx!.createOscillator();
            osc.frequency.value = 220;
            osc.type = "sawtooth";
            analyser = audioCtx!.createAnalyser();
            analyser.fftSize = 2048;
            analyser.smoothingTimeConstant = 0.7;
            osc.connect(analyser);
            // Modulate the synth frequency from a slow LFO + something
            // like a drum beat — gives a visible signal without a mic.
            const lfo = audioCtx!.createOscillator();
            const lfoGain = audioCtx!.createGain();
            lfo.frequency.value = 2.4;
            lfoGain.gain.value = 80;
            lfo.connect(lfoGain).connect(osc.frequency);
            const kick = audioCtx!.createOscillator();
            const kickGain = audioCtx!.createGain();
            kick.frequency.value = 0.6;
            kickGain.gain.value = 800;
            kick.connect(kickGain).connect(osc.frequency);
            osc.start(); lfo.start(); kick.start();
          });
        }
      } catch (e) { /* ignore */ }
      audioRequested = false; // consumed
    }

    // Read FFT if analyser is up
    if (analyser) {
      analyser.getByteFrequencyData(audioBuf);
    } else {
      // No analyser yet — fade bars to zero
      for (let i = 0; i < audioBars.length; i++) {
        const bar = audioBars[i]!;
        const target = 0;
        const cur = bar.h;
        bar.h = cur + (target - cur) * 0.12;
        bar.opacity = 0.25;
        bar.filter!.brightness = 1.0;
      }
      return;
    }

    // Render: bin index maps to color, height to bin magnitude. We
    // sample 32 evenly spaced bins from the 1024-bin FFT.
    const stride = Math.floor(audioBuf.length / 2 / AUDIO_BINS);
    for (let i = 0; i < AUDIO_BINS; i++) {
      const bar = audioBars[i]!;
      let sum = 0;
      for (let j = 0; j < stride; j++) {
        sum += audioBuf[i * stride + j] ?? 0;
      }
      const v = (sum / stride) / 255; // 0..1
      const targetH = 20 + v * 160; // bars 20-180px tall
      const cur = bar.h;
      bar.h = cur + (targetH - cur) * 0.45; // smoothing
      // Slight horizontal gradient phase per bin
      bar.gradientAngle = Math.PI / 2 + (i * 0.06);
      bar.opacity = 0.5 + v * 0.5;
      // Filter chain reacts to magnitude — louder = warmer hue rotation
      bar.filter!.brightness = 1.0 + v * 0.4;
      bar.filter!.hueRotate = (i / AUDIO_BINS) * Math.PI * 1.5 + v * 0.6;
      bar.filter!.saturate = 1.4 + v * 0.6;
      // Color: bass=warm, mids=yellow, treble=cool
      const t = i / (AUDIO_BINS - 1);
      if (t < 0.5) {
        // Warm: red→yellow
        const tt = t * 2;
        bar.fill = { r: 0.96 + tt * 0.02, g: 0.30 + tt * 0.55, b: 0.20 - tt * 0.10, a: 0.85 };
      } else {
        // Yellow→cool
        const tt = (t - 0.5) * 2;
        bar.fill = { r: 0.98 - tt * 0.78, g: 0.85 - tt * 0.40, b: 0.10 + tt * 0.85, a: 0.85 };
      }
    }
  };
  addNode(scene, audioTick);
  // Toggle audio on click in audio mode
  const audioClick = makeRect({
    x: -10, y: -10, w: 1, h: 1, opacity: 0,
    fill: { r: 0, g: 0, b: 0, a: 0 },
  });
  audioClick.update = () => {
    if (currentMode !== "audio") return;
    if (pDown && !audioRequested) {
      audioRequested = true;
    }
  };
  addNode(scene, audioClick);

  // Audio overlay — "TAP TO START" hint that disappears once running.
  let audioOverlayVisible = true;
  const audioOverlay = makeRect({
    x: 0, y: 0, w, h,
    radius: 0,
    fill: { r: 0.02, g: 0.04, b: 0.08, a: 0.65 },
  });
  addNode(scene, audioOverlay);
  let tapLabel: SceneNode = makeText({
    x: 0, y: 0, w: w, h: 100,
    text: "TAP TO START AUDIO INPUT",
    fontSize: 32,
    fontWeight: 700,
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    textAlign: "center",
    letterSpacing: 0.32,
    fontFamily: "'Times New Roman', Georgia, serif",
  });
  addNode(scene, tapLabel);
  const audioOverlayTick = makeRect({
    x: -10, y: -10, w: 1, h: 1, opacity: 0,
    fill: { r: 0, g: 0, b: 0, a: 0 },
  });
  audioOverlayTick.update = () => {
    if (currentMode === "audio" && audioStarted && audioCtx) {
      audioOverlay.opacity = Math.max(0, audioOverlay.opacity - 0.05);
      tapLabel.opacity = audioOverlay.opacity;
      if (audioOverlay.opacity <= 0.01) audioOverlayVisible = false;
    } else if (currentMode === "audio" && audioOverlayVisible) {
      audioOverlay.opacity = 0.65;
      tapLabel.opacity = 1;
    } else if (currentMode !== "audio" && audioOverlayVisible) {
      audioOverlay.opacity = 0;
      tapLabel.opacity = 0;
    }
  };
  addNode(scene, audioOverlayTick);
  // Subtitle below tap label
  const tapSub = makeText({
    x: 0, y: h / 2 + 30, w: w, h: 24,
    text: "(mic input — synth fallback if denied)",
    fontSize: 13,
    fontWeight: 400,
    textColor: { r: 1, g: 1, b: 1, a: 0.55 },
    textAlign: "center",
    letterSpacing: 0.04,
    fontFamily: "system-ui, -apple-system, sans-serif",
  });
  addNode(scene, tapSub);
  tapSub.update = (n) => {
    n.opacity = currentMode === "audio" && audioOverlayVisible ? 0.55 : 0;
  };

  // ============================================================
  // 3i. CAPTURE — click anywhere to download the current canvas as
  //     a real PNG file. Uses canvas.toDataURL → Blob → download. Also
  //     tracks the most-recent capture for a "LAST CAPTURE" preview
  //     thumbnail that appears at the bottom of the canvas.
  // ============================================================
  // Container for the download image preview state. Updated each click.
  let lastCaptureUrl: string | null = null;
  let lastCaptureFlash = 0; // counts down over 0.8s after a capture
  let captureCounter = 0;

  // Big "DOWNLOAD NOW" card filling most of the canvas.
  const captureCard = makeRect({
    x: pad, y: pad + 28,
    w: w - pad * 2, h: h - 80 - pad * 2 - 28,
    radius: 24,
    fill: { r: 0.04, g: 0.04, b: 0.07, a: 0.85 },
    borderWidths: [2, 2, 2, 2],
    borderColor: { r: 0.96, g: 0.55, b: 0.20, a: 0.7 },
  });
  addNode(scene, captureCard);
  // Title
  const captureTitle = makeText({
    x: pad + 32, y: pad + 56, w: w - pad * 2 - 64, h: 80,
    text: "DOWNLOAD THE\nCURRENT CANVAS",
    fontSize: 56,
    fontWeight: 800,
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    lineHeight: 1.05,
    letterSpacing: -0.01,
    fontFamily: "'Times New Roman', Georgia, serif",
  });
  addNode(scene, captureTitle);
  // Subtitle
  const captureSubtitle = makeText({
    x: pad + 32, y: pad + 56 + 130, w: w - pad * 2 - 64, h: 60,
    text: "Click anywhere to capture the current frame\nas a real PNG download — NSPP-generated art, ready to ship.",
    fontSize: 14,
    fontWeight: 400,
    textColor: { r: 1, g: 1, b: 1, a: 0.65 },
    lineHeight: 1.5,
    fontFamily: "system-ui, -apple-system, sans-serif",
  });
  addNode(scene, captureSubtitle);
  // Filename preview chip
  const captureFilename = makeText({
    x: pad + 32, y: h - 80 - 80, w: w - pad * 2 - 64, h: 28,
    text: "next file: nspp-capture-0.png",
    fontSize: 12,
    fontWeight: 600,
    textColor: { r: 0.96, g: 0.55, b: 0.20, a: 0.95 },
    letterSpacing: 0.12,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  });
  addNode(scene, captureFilename);
  captureFilename.update = (n) => {
    n.text = `next file: nspp-capture-${captureCounter}.png`;
  };

  // Click handler — capture and download.
  const captureClick = makeRect({
    x: -10, y: -10, w: 1, h: 1, opacity: 0,
    fill: { r: 0, g: 0, b: 0, a: 0 },
  });
  let wasDownCapture = false;
  let lastCaptureTime = -10;
  captureClick.update = () => {
    if (currentMode !== "capture") {
      wasDownCapture = false;
      return;
    }
    if (pDown && !wasDownCapture) {
      // Trigger capture by side-door. main.ts wires a window.__nsppCapture
      // function that does canvas.toDataURL + download. We just bump the
      // counter and flash here.
      captureCounter++;
      lastCaptureFlash = 1.0;
      lastCaptureTime = performance.now() / 1000;
      const cb = (globalThis as unknown as { __nsppCapture?: (n: number) => void }).__nsppCapture;
      if (cb) cb(captureCounter);
    }
    wasDownCapture = pDown;
  };
  addNode(scene, captureClick);

  // Flash overlay — bright gold flash on capture success.
  const captureFlash = makeRect({
    x: 0, y: 0, w, h,
    radius: 0,
    fill: { r: 0.96, g: 0.55, b: 0.20, a: 0 },
  });
  addNode(scene, captureFlash);
  const flashTick = makeRect({
    x: -10, y: -10, w: 1, h: 1, opacity: 0,
    fill: { r: 0, g: 0, b: 0, a: 0 },
  });
  flashTick.update = () => {
    if (currentMode !== "capture") {
      captureFlash.opacity = 0;
      return;
    }
    if (lastCaptureFlash > 0) {
      captureFlash.opacity = lastCaptureFlash * 0.32;
      lastCaptureFlash = Math.max(0, lastCaptureFlash - 0.06);
      if (lastCaptureFlash <= 0.01) {
        captureFlash.opacity = 0;
        captureFlashVisible = false;
      }
    }
  };
  addNode(scene, flashTick);

  // "✓ SAVED nspp-capture-N.png" toast chip — top-right.
  const captureToast = makeText({
    x: w - pad - 240, y: pad + 24, w: 240, h: 24,
    text: "",
    fontSize: 10,
    fontWeight: 700,
    textColor: { r: 0.55, g: 0.96, b: 0.74, a: 1 },
    textAlign: "right",
    letterSpacing: 0.22,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  });
  addNode(scene, captureToast);
  const toastTick = makeRect({
    x: -10, y: -10, w: 1, h: 1, opacity: 0,
    fill: { r: 0, g: 0, b: 0, a: 0 },
  });
  toastTick.update = () => {
    if (currentMode === "capture" && captureCounter > 0) {
      captureToast.text = `✓ saved nspp-capture-${captureCounter}.png`;
      // Fade after 1s of "saved" state
      captureToast.opacity = Math.max(0, Math.min(1, 1.0 - (performance.now() / 1000 - lastCaptureTime) * 0.5));
    } else {
      captureToast.text = "";
    }
  };
  addNode(scene, toastTick);

  // ============================================================
  // 4. BOTTOM TAB BAR — 4 tap-to-switch mode buttons
  // ============================================================
  const tabY = h - 80;
  const tabH = 56;
  const tabGap = 12;
  const tabW = (w - pad * 2 - tabGap * (MODES.length - 1)) / MODES.length;
  for (let i = 0; i < MODES.length; i++) {
    const m = MODES[i];
    const tx = pad + i * (tabW + tabGap);
    const tab = makeRect({
      x: tx, y: tabY, w: tabW, h: tabH,
      radius: 16,
      fill: { r: 1, g: 1, b: 1, a: 0.06 },
      borderWidths: [1, 1, 1, 1],
      borderColor: { r: 1, g: 1, b: 1, a: 0.14 },
    });
    tab.onTap = () => setMode(m.key);
    tab.update = (n) => {
      const isActive = currentMode === m.key;
      n.fill = isActive
        ? m.gradient[0]
        : { r: 1, g: 1, b: 1, a: 0.06 };
      n.borderColor = isActive
        ? { r: m.gradient[1].r, g: m.gradient[1].g, b: m.gradient[1].b, a: 0.9 }
        : { r: 1, g: 1, b: 1, a: 0.14 };
      n.strokeWidth = isActive ? 2 : 1;
      n.stroke.a = isActive ? 1 : 0;
    };
    addNode(scene, tab); z++;
    const tabLabel = makeText({
      x: tx, y: tabY, w: tabW, h: tabH,
      text: m.label,
      fontSize: 16,
      fontWeight: 700,
      textColor: { r: 1, g: 1, b: 1, a: 0.9 },
      textAlign: "center",
      letterSpacing: 0.02,
      fontFamily: "system-ui, -apple-system, sans-serif",
    });
    tabLabel.update = (n) => {
      const isActive = currentMode === m.key;
      n.textColor = { r: 1, g: 1, b: 1, a: isActive ? 1 : 0.65 };
    };
    addNode(scene, tabLabel); z++;
  }

  return scene;
}

void (null as unknown as SceneNode);
